import { useMemo } from 'react';
import { useApps, useAppsOfProjects, useBuilds, useEnvResolutions, useMe, useProfiles, useProfilesOfApps, useProjects, useProviderAccounts } from '@/api/hooks';
import type { Build, ProviderAccount } from '@/api/schemas';
import { featureFrom, useCan } from '@/lib/permissions';
import { seesProviders } from '@/lib/audience';
import { useBuildsLive } from '@/features/builds/useBuildsLive';
import { buildCount, buildFacts, bundleIdOf, byId, isEnvSettled, isPending, isPlatformOperator, isRunnable, liveEnvironments, pickBySlug, platformFor, runProfile, usableAccountsOf, type EnvTile } from './setupModel';
import { progressOf, readinessOf, stepStates, type SetupFacts, type StepId, type StepState } from './steps';

// The pure half lives in setupModel.ts; these are re-exported because other modules import them from here.
export { bundleIdOf, teamKeys, type EnvTile } from './setupModel';

export interface SetupSearch {
  project?: string;
  app?: string;
}

type Lists = ReturnType<typeof useSetupLists>;
type Env = ReturnType<typeof useEnvTiles>;
type Activity = ReturnType<typeof useSetupActivity>;

/** The lists every step manages, for the project and app in the URL (`?project=&app=`, first ones when absent). */
function useSetupLists(search: SetupSearch) {
  const projects = useProjects();
  const accounts = useProviderAccounts();
  const project = pickBySlug(projects.data, search.project);
  const apps = useApps(project?.id);
  const app = pickBySlug(apps.data, search.app);
  const profiles = useProfiles(app?.id);
  const appsOfProjects = useAppsOfProjects((projects.data ?? []).map((p) => p.id));
  const profilesOfApps = useProfilesOfApps((apps.data ?? []).map((a) => a.id));
  return { projects, accounts, project, apps, app, profiles, appsOfProjects, profilesOfApps };
}

/** One tile per live environment: its effective env for the selected app (`POST /apps/{id}/env/resolve`). */
function useEnvTiles({ project, app, profiles }: Lists) {
  const canReadEnv = useCan('env:read', project?.id);
  const environments = useMemo(() => liveEnvironments(project), [project]);
  const profileList = useMemo(() => profiles.data ?? [], [profiles.data]);
  const targets = useMemo(() => environments.map((e) => ({ environment: e.slug, platform: platformFor(e, profileList) })), [environments, profileList]);
  const resolutions = useEnvResolutions(app?.id, targets, canReadEnv.allowed);
  const envTiles: EnvTile[] = environments.map((environment, i) => ({ environment, platform: targets[i]!.platform, resolution: resolutions[i]?.data, loading: Boolean(resolutions[i]?.isLoading), error: resolutions[i]?.error ?? null }));
  return { canReadEnv, environments, profileList, envTiles };
}

/** The app's builds (the latest and the succeeded one) and the live stream of the project's builds. */
function useSetupActivity({ project, app }: Lists) {
  const enabled = Boolean(project && app);
  const builds = useBuilds({ projectId: project?.id, app: app?.slug, limit: 5 }, enabled);
  const succeeded = useBuilds({ projectId: project?.id, app: app?.slug, status: ['succeeded'], limit: 1 }, enabled);
  const liveStatus = useBuildsLive(project?.id);
  return { builds, succeeded, liveStatus };
}

interface Reads {
  me: ReturnType<typeof useMe>;
  lists: Lists;
  env: Env;
  activity: Activity;
  bitrise: boolean;
  /** Every provider account (EAS and Bitrise) for the table; only the usable ones count as done. */
  providerAccounts: ProviderAccount[];
  usableAccounts: ProviderAccount[];
}

/** How many projects, apps and (runnable) profiles the lists hold. */
function listCounts({ lists, env, bitrise }: Reads) {
  return {
    projects: lists.projects.data?.length ?? 0,
    apps: lists.apps.data?.length ?? 0,
    profiles: env.profileList.length,
    runnableProfiles: env.profileList.filter((p) => isRunnable(p, bitrise)).length,
  };
}

/** The app's builds, as the checklist counts them (undefined = not read). */
function activityCounts(r: Reads, buildList: Build[] | undefined) {
  const { succeeded } = r.activity;
  return {
    builds: buildCount(r.lists.app?.lastBuildNumber, buildList?.length),
    buildsSucceeded: succeeded.data?.pages[0]?.data.length ?? 0,
  };
}

/** The facts the checklist is computed from (plus the pieces they were read from), and whether a read is still pending. */
function readFacts(r: Reads) {
  const { app } = r.lists;
  const { builds } = r.activity;
  const buildList = builds.data?.pages[0]?.data;
  const envSettled = isEnvSettled(Boolean(app), r.env.canReadEnv.allowed, r.env.envTiles);
  const facts = buildFacts({ ...listCounts(r), ...activityCounts(r, buildList), usableAccounts: r.usableAccounts.length, envTiles: r.env.envTiles, envSettled });
  const pending = isPending({ canReadEnv: r.env.canReadEnv.allowed, hasApp: Boolean(app), envSettled, buildsLoading: builds.isLoading });
  return { buildList, facts, pending };
}

/** Steps, progress, readiness and the per-project / per-app counts the hub shows. */
function readViews(r: Reads, facts: SetupFacts) {
  const { projects, project, apps, app, appsOfProjects, profilesOfApps } = r.lists;
  const states = stepStates(facts);
  const names = { accounts: seesProviders(r.me.data) ? r.usableAccounts.map((a) => a.externalAccount ?? a.name) : [], project: project?.name, app: app?.name, person: r.me.data?.user?.name };
  return {
    states,
    state: (id: StepId) => states.find((s) => s.id === id) as StepState,
    progress: progressOf(states),
    readiness: readinessOf(facts, names),
    appCounts: byId(projects.data, appsOfProjects, (list) => list?.length),
    bundleIds: byId(apps.data, profilesOfApps, bundleIdOf),
  };
}

/** The project, app and lists as the hub exposes them (without the per-project / per-app query results). */
const listsOut = ({ projects, project, accounts, apps, app, profiles }: Lists) => ({ projects, project, accounts, apps, app, profiles });

/**
 * Everything the setup hub reads, for the project and app in the URL (`?project=&app=`, first ones when absent):
 * the lists every step manages, the facts the checklist is computed from, and the derived states, progress and
 * readiness. Builds stay live through the SSE `builds` topic.
 */
export function useSetup(search: SetupSearch) {
  const me = useMe();
  const lists = useSetupLists(search);
  const env = useEnvTiles(lists);
  const activity = useSetupActivity(lists);
  const bitrise = featureFrom(me.data, 'bitrise') === true;
  const providerAccounts = lists.accounts.data ?? [];
  const reads: Reads = { me, lists, env, activity, bitrise, providerAccounts, usableAccounts: usableAccountsOf(providerAccounts, bitrise) };
  const read = readFacts(reads);
  return {
    me,
    ...listsOut(lists),
    providerAccounts,
    /** `me.features.bitrise` / `runBuild`: Bitrise builds and the console's Run build are served by this Hangar. */
    bitrise,
    runBuild: featureFrom(me.data, 'runBuild') === true,
    /** The profile First build starts (Run build): the first runnable one, else the first. */
    profile: runProfile(env.profileList, bitrise),
    /** Malible platform operators set up provider accounts and see the local-stack notes. */
    operator: isPlatformOperator(me.data),
    environments: env.environments,
    envTiles: env.envTiles,
    canReadEnv: env.canReadEnv,
    builds: activity.builds,
    liveStatus: activity.liveStatus,
    ...read,
    ...readViews(reads, read.facts),
  };
}

export type Setup = ReturnType<typeof useSetup>;
